import React from 'react';
import { Box, Button, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useProfile } from '../auth/ProfileContext';
import { AccessStatus } from '../properties/api';

const copy: Record<Exclude<AccessStatus, 'approved'>, { title: string; message: string }> = {
  pending: {
    title: 'The photography platform is in private testing',
    message: 'Creator tools are available to invited testers. Your account and assigned client deliveries remain available.',
  },
  rejected: {
    title: 'Workspace access was not approved',
    message: 'This account cannot use the creator workspace. Contact iLand Studio if this looks incorrect.',
  },
  inactive: {
    title: 'This account is inactive',
    message: 'Workspace access is paused. Existing client gallery links remain available.',
  },
};

const RequireWorkspaceAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { signOut } = useAuth();
  const { profile, loading, error } = useProfile();
  const status: AccessStatus | 'loading' | 'error' = loading
    ? 'loading'
    : error || !profile
      ? 'error'
      : profile.accessStatus;

  if (status === 'loading') {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (status === 'approved' && profile?.role !== 'client' && profile?.studioAccess !== false) return <>{children}</>;

  if (profile?.role === 'client') {
    return (
      <Box maxW="620px" mx="auto" px={4} py={{ base: 14, md: 24 }}>
        <VStack spacing={4} textAlign="center">
          <Heading size="lg">This is a client account</Heading>
          <Text color="gray.500">Your private deliveries are in Shared with me. Editing tools are reserved for creator accounts.</Text>
          <Button as={RouterLink} to="/my-galleries" colorScheme="orange">Shared with me</Button>
        </VStack>
      </Box>
    );
  }

  const content = status === 'error'
    ? { title: 'Could not check account access', message: 'Please try again in a moment.' }
    : status === 'approved'
      ? { title: 'Creator access required', message: 'This account does not have access to the creator workspace.' }
      : copy[status];

  return (
    <Box maxW="620px" mx="auto" px={4} py={{ base: 14, md: 24 }}>
      <VStack spacing={4} textAlign="center">
        <Heading size="lg">{content.title}</Heading>
        <Text color="gray.500">{content.message}</Text>
        <HStack>
          <Button as={RouterLink} to="/account" colorScheme="orange">
            My account
          </Button>
          {status === 'error' ? (
            <Button variant="outline" onClick={() => window.location.reload()}>
              Try again
            </Button>
          ) : (
            <Button variant="outline" onClick={() => signOut()}>
              Sign out
            </Button>
          )}
        </HStack>
      </VStack>
    </Box>
  );
};

export default RequireWorkspaceAccess;
